import * as Moment from "moment";
import { IClaioRepository, IClaioImageSource, ClaioSchemaGroup } from "../../../domain/claio";
import { IClaioImageHeaderBean } from "./beans/IClaioImageHeaderBean";
import { DateDuration } from "../../../domain/system";
import { ClaioImageSource } from ".";
import { ImageSourceKinds } from "../../../domain/document";
import { AuthenticatedService } from "../../../infrastructure";
import { IClaioImageStockable } from "../IClaioImageStockable";
import { ClaioSchemaInformationTranslator } from "./translators";

export class ClaioRepository extends AuthenticatedService implements IClaioRepository, IClaioImageStockable {
  constructor(getToken: () => string) {
    super(getToken);
  }

  async getPatientRelatedClaioImages(patientId: string, duration: DateDuration, departmentId: string, orderNo: string, shouldOrderByAscending: boolean): Promise<IClaioImageSource[]> {
    if (!patientId || patientId === "") { throw new Error("id is empty!!!"); }
    const url = "/api/patient/" + patientId + "/claio/headers";
    const query = {
      params: {
        token: this.token,
        startDate: this.toDateText(duration ? duration.from : null),
        endDate: this.toDateText(duration ? duration.to : null),
        orderNo,
        departmentCode: departmentId,
        shouldOrderByAscending,
      }
    };
    const result = await this.get(url, query);
    const converted = this.toClaioImageSources(patientId, result.data);
    this.stock(converted);
    return this.toClaioImageSources(patientId, result.data);
  }

  public async getClaioImageSource(patientId: string, imageId: string): Promise<IClaioImageSource> {
    const url = "/api/patient/" + patientId + "/claio/header/" + imageId;
    const query = {
      params: {
        token: this.token,
      }
    };
    const result = await this.get(url, query);
    return this.toClaioImageSource(patientId, result.data);
  }

  private toDateText(value: Date): string {
    if (!value) { return null; }
    // yyyyMMdd
    const year = value.getFullYear();
    const month = ("0" + (value.getMonth() + 1)).slice(-2);
    const date = ("0" + value.getDate()).slice(-2);
    return year + month + date;
  }

  private toClaioImageSources(patientId: string, beans: IClaioImageHeaderBean[]): IClaioImageSource[] {
    const results: IClaioImageSource[] = [];
    for (const bean of beans) {
      const imageSource = this.toClaioImageSource(patientId, bean);
      results.push(imageSource);
    }
    return results;
  }

  private toClaioImageSource(patientId: string, bean: IClaioImageHeaderBean): IClaioImageSource {
    return new ClaioImageSource(
      () => this.token,
      patientId,
      (bean.imageId != null) ? bean.imageId.toString() : null,
      null,
      ImageSourceKinds.Cache,
      !!bean.isMovie,
      !!bean.isLeft,
      !!bean.isRight,
      !!bean.isSecret,
      bean.departmentCode,
      false,
      this.toDate(bean.imageDate),
      bean.imageKind,
    );
  }

  private toDate(imageDate: string): Date {
    if (!imageDate) { return null; }
    // 現状ではバックエンドでyyyyMMddを指定している
    return Moment(imageDate, "YYYYMMDD").toDate();
  }

  private stock(source: IClaioImageSource[]): void {
    for (const s of source) {
      if (!this._stocked[s.patientId]) {
        this._stocked[s.patientId] = {};
      }
      this._stocked[s.patientId][s.claioImageKey] = s;
    }
  }

  private readonly _stocked: { [key: string]: { [key: string]: IClaioImageSource } } = {};

  public getStockedClaioImage(patientId: string, imageId: string): IClaioImageSource {
    if (!patientId) {
      throw new Error("patientId is empty!!!");
    }
    if (!imageId) {
      throw new Error("imageId is empty!!!");
    }
    return this._stocked[patientId]
      ? this._stocked[patientId][imageId]
      : null;
  }
  
  public async getClaioSchemaGroups(departmentId: string): Promise<ClaioSchemaGroup[]> {
    const url = "/api/claio/schema-templates";
    const query = {
      params: {
        token: this.token,
        departmentCode: departmentId,
      }
    };
    const result = await this.get(url, query);
    const translator = new ClaioSchemaInformationTranslator(() => this.token);
    return translator.toClaioSchemaGroups(result.data);
  }
}
